import React, { useState, useCallback } from 'react';
import './Frequency.css';
import topicsData from '../data/topics.json';

const Frequency: React.FC = () => {
  const [currentTopic, setCurrentTopic] = useState<string>('');
  const [currentNumber, setCurrentNumber] = useState<number | null>(null);
  const [isNumberVisible, setIsNumberVisible] = useState(false);
  const [gameState, setGameState] = useState<'topic' | 'number' | 'hidden'>('topic');
  const [hoveredNumber, setHoveredNumber] = useState<number | null>(null);
  const [userGuess, setUserGuess] = useState<number | null>(null);
  const [score, setScore] = useState<number>(0);
  const [showScore, setShowScore] = useState<boolean>(false);

  // List of random topics loaded from JSON file
  const topics = topicsData;

  const generateRandomTopic = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * topics.length);
    setCurrentTopic(topics[randomIndex]);
    setCurrentNumber(null);
    setIsNumberVisible(false);
    setGameState('topic');
    setUserGuess(null);
    setShowScore(false);
    setHoveredNumber(null);
  }, [topics]);

  const generateRandomNumber = () => {
    const randomNumber = Math.floor(Math.random() * 101); // 0-100
    setCurrentNumber(randomNumber);
    setIsNumberVisible(true);
    setGameState('number');
    setUserGuess(null);
    setShowScore(false);
    setHoveredNumber(null);
  };

  const getDisplayPosition = (number: number) => {
    // Convert -10 to 110 range to 0-100% display position
    return ((number) / 100) * 100;
  };

  const toggleNumberVisibility = () => {
    if (gameState === 'topic') {
      generateRandomNumber();
    } else if (gameState === 'number') {
      setIsNumberVisible(false);
      setGameState('hidden');
    } else if (gameState === 'hidden') {
      setIsNumberVisible(true);
      setGameState('number');
    }
  };

  const resetGame = () => {
    if (currentTopic) {
      generateRandomNumber();
    }
  };

  const handleRulerClick = (number: number) => {
    if (currentNumber !== null && gameState !== 'topic') {
      setUserGuess(number);
      setShowScore(true);
      
      // Calculate score: 5 points minus the distance (minimum 0)
      const distance = Math.abs(currentNumber - number);
      const points = Math.max(0, 5 - distance);
      setScore(prevScore => prevScore + points);
    }
  };

  const handleRulerHover = (number: number) => {
    // Cap the hover number at 0-100 limits
    const cappedNumber = Math.max(0, Math.min(100, number));
    setHoveredNumber(cappedNumber);
  };

  const handleRulerLeave = () => {
    // Don't clear hover number - keep it visible
  };

  const handleRulerTouch = (number: number) => {
    // For mobile devices, set the hover number on touch
    const cappedNumber = Math.max(0, Math.min(100, number));
    setHoveredNumber(cappedNumber);
  };

  // Generate initial topic on component mount
  React.useEffect(() => {
    generateRandomTopic();
  }, [generateRandomTopic]);

  const getGradientColor = (number: number) => {
    // Create a gradient from red (0) to yellow (50) to green (100)
    // Clamp number to 0-100 range for color calculation
    const clampedNumber = Math.max(0, Math.min(100, number));
    if (clampedNumber <= 50) {
      const intensity = Math.floor((clampedNumber / 50) * 255);
      return `rgb(255, ${intensity}, 0)`; // Red to Yellow
    } else {
      const intensity = Math.floor(((clampedNumber - 50) / 50) * 255);
      return `rgb(${255 - intensity}, 255, 0)`; // Yellow to Green
    }
  };

  return (
    <div className="frequency-container">
      <div className="frequency-content">
        <h1 className="frequency-title">Frequency Game</h1>
        
        <div className="topic-section">
          <h2 className="topic-label">Current Topic:</h2>
          <div className="topic-display">
            <span className="topic-text">{currentTopic}</span>
            <button 
              className="refresh-button"
              onClick={generateRandomTopic}
              title="Generate new topic"
            >
              🔄
            </button>
          </div>
        </div>

        <div className="game-section">
          <div className="score-display">
            <span className="score-label">Score: {score}</span>
          </div>
          
          <button 
            className="game-button"
            onClick={toggleNumberVisibility}
          >
            {gameState === 'topic' ? 'Generate Frequency' : 
             gameState === 'number' ? 'Hide Number' : 'Show Number'}
          </button>

          {gameState !== 'topic' && (
            <button 
              className="reset-button"
              onClick={resetGame}
            >
              New Number
            </button>
          )}
        </div>


        {currentNumber !== null && (
          <div className="ruler-section">
            <div className="ruler-container">
              <div className="ruler-gradient">
                {Array.from({ length: 101 }, (_, i) => {
                  const number = i; // Range from 0 to 100
                  const isClickable = number >= 0 && number <= 100;
                  return (
                    <div
                      key={i}
                      className={`ruler-segment ${isClickable ? 'clickable' : ''}`}
                      style={{ backgroundColor: getGradientColor(number) }}
                      onMouseEnter={isClickable ? () => handleRulerHover(number) : undefined}
                      onMouseLeave={isClickable ? handleRulerLeave : undefined}
                      onClick={isClickable ? () => handleRulerClick(number) : undefined}
                      onTouchStart={isClickable ? () => handleRulerTouch(number) : undefined}
                    />
                  );
                })}
              </div>
              {isNumberVisible && (
                <div 
                  className="number-marker"
                  style={{ 
                    left: `${getDisplayPosition(currentNumber)}%`,
                    backgroundColor: getGradientColor(currentNumber)
                  }}
                >
                  {currentNumber}
                </div>
              )}
            </div>
            {hoveredNumber !== null && (
              <div 
                className="hover-marker"
                style={{ 
                  left: `${getDisplayPosition(hoveredNumber)}%`,
                  backgroundColor: getGradientColor(hoveredNumber)
                }}
              >
                {hoveredNumber}
              </div>
            )}
            <div className="ruler-labels">
              <span>0</span>
              <span>50</span>
              <span>100</span>
            </div>
          </div>
        )}

        {showScore && userGuess !== null && currentNumber !== null && (
            <div className="score-result">
              <div className="score-info">
                <span>Your guess: {userGuess}</span>
                <span>Actual: {currentNumber}</span>
                <span>Distance: {Math.abs(currentNumber - userGuess)}</span>
                <span className="points-earned">
                  Points: +{Math.max(0, 5 - Math.abs(currentNumber - userGuess))}
                </span>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default Frequency; 